import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import Input from '../../components/form/input/InputField';

interface Driver {
  id: string;
  fullName: string;
  mobileNumber: string;
  communicationEmail: string;
  licenseNumber?: string;
  isAvailable: boolean;
  subcompanyId?: string | null;
  subcompany?: { id: string; name: string } | null;
}

interface SortConfig {
  key: keyof Driver | '';
  direction: 'asc' | 'desc';
}

const DriverList: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
  const [paginatedDrivers, setPaginatedDrivers] = useState<Driver[]>([]);

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    handleFilterSortPaginate();
  }, [searchTerm, currentPage, pageSize, sortConfig, drivers.length]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/drivers');
      setDrivers(response.data);
    } catch {
      setError('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSortPaginate = () => {
    let filtered = drivers.filter(driver =>
      driver.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.communicationEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (sortConfig.key !== '') {
      filtered = filtered.sort((a, b) => {
        const aValue = String(a[sortConfig.key as keyof Driver] ?? '');
        const bValue = String(b[sortConfig.key as keyof Driver] ?? '');
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }
    const totalItems = filtered.length;
    const calculatedTotalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setTotalPages(calculatedTotalPages);
    setPaginatedDrivers(filtered.slice(startIndex, endIndex));
  };

  const handleSort = (key: keyof Driver) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortIcon = (key: keyof Driver) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? 'bg-brand-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 mx-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        await axios.delete(`/drivers/${id}`);
        fetchDrivers();
      } catch {
        setError('Failed to delete driver');
      }
    }
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Drivers"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to="/drivers/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Add Driver
        </Link>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="p-4 border-b">
          <Input
            placeholder="Search drivers..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {error && (
          <div className="border-b border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('fullName')}
                >
                  Name {renderSortIcon('fullName')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('communicationEmail')}
                >
                  Email {renderSortIcon('communicationEmail')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('mobileNumber')}
                >
                  Phone {renderSortIcon('mobileNumber')}
                </th>
                <th className="px-5 py-3 text-end text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    </div>
                  </td>
                </tr>
              ) : paginatedDrivers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No drivers found
                  </td>
                </tr>
              ) : (
                paginatedDrivers.map(driver => (
                  <tr key={driver.id}>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{driver.fullName}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{driver.communicationEmail}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">{driver.mobileNumber}</td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <Link
                        to={`/drivers/${driver.id}/view`} className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                      >
                        View
                      </Link>
                      <Link
                        to={`/drivers/${driver.id}`} className="text-green-600 hover:underline"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(driver.id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && paginatedDrivers.length > 0 && (
          <div className="px-6 py-4 border-t">
            <div className="flex justify-between items-center">
              <div>
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="border rounded px-2 py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverList; 